/**
 * Addon Plan Card Web Component
 *
 * Displays queued addon plan details including data amount, duration, expiry date, and status.
 *
 * Usage:
 * <addon-plan-card
 *   plan-title="..."
 *   data-amount="..."
 *   duration="..."
 *   expires-on="..."
 *   status="..."
 *   label-data="Data"
 *   label-duration="Duration"
 *   label-expires="Expires on"
 *   label-status="Status"
 *   container-bg-color="#FFFFFF"
 *   container-bg-transparent="false"
 *   container-padding="20"
 *   container-border-radius="8"
 *   container-border-width="0"
 *   container-border-type="solid"
 *   container-border-color="#E0E0E0"
 *   container-border-gradient-start="#5b4db3"
 *   container-border-gradient-end="#8b7dd8"
 *   container-border-gradient-angle="135"
 *   title-text-color="#000000"
 *   title-text-size="18"
 *   title-text-weight="600"
 *   key-text-color="#666666"
 *   key-text-size="14"
 *   key-text-weight="400"
 *   value-text-color="#000000"
 *   value-text-size="14"
 *   value-text-weight="500"
 *   row-gap="12"
 *   row-border-color="#E0E0E0"
 *   row-border-width="1"
 * ></addon-plan-card>
 */

class AddonPlanCard extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
  }

  static get observedAttributes() {
    return [
      'plan-title', 'data-amount', 'duration', 'expires-on', 'status',
      'label-data', 'label-duration', 'label-expires', 'label-status',
      'container-bg-color', 'container-bg-transparent', 'container-padding', 'container-border-radius',
      'container-border-width', 'container-border-type', 'container-border-color',
      'container-border-gradient-start', 'container-border-gradient-end', 'container-border-gradient-angle',
      'title-text-color', 'title-text-size', 'title-text-weight',
      'key-text-color', 'key-text-size', 'key-text-weight',
      'value-text-color', 'value-text-size', 'value-text-weight',
      'row-gap', 'row-border-color', 'row-border-width'
    ];
  }

  attributeChangedCallback() {
    this.render();
  }

  render() {
    // Get attributes
    const planTitle = this.getAttribute('plan-title') || '';
    const dataAmount = this.getAttribute('data-amount') || '';
    const duration = this.getAttribute('duration') || '';
    const expiresOn = this.getAttribute('expires-on') || '';
    const status = this.getAttribute('status') || '';

    // Labels
    const labelData = this.getAttribute('label-data') || 'Data';
    const labelDuration = this.getAttribute('label-duration') || 'Duration';
    const labelExpires = this.getAttribute('label-expires') || 'Expires on';
    const labelStatus = this.getAttribute('label-status') || 'Status';

    // Styling attributes
    const containerBgColor = this.getAttribute('container-bg-color') || '#FFFFFF';
    const containerBgTransparent = this.getAttribute('container-bg-transparent') === 'true';
    const containerPadding = this.getAttribute('container-padding') || '20';
    const containerBorderRadius = this.getAttribute('container-border-radius') || '8';
    const containerBorderWidth = this.getAttribute('container-border-width') || '0';
    const containerBorderType = this.getAttribute('container-border-type') || 'solid';
    const containerBorderColor = this.getAttribute('container-border-color') || '#E0E0E0';
    const containerBorderGradientStart = this.getAttribute('container-border-gradient-start') || '#5b4db3';
    const containerBorderGradientEnd = this.getAttribute('container-border-gradient-end') || '#8b7dd8';
    const containerBorderGradientAngle = this.getAttribute('container-border-gradient-angle') || '135';
    const titleTextColor = this.getAttribute('title-text-color') || '#000000';
    const titleTextSize = this.getAttribute('title-text-size') || '18';
    const titleTextWeight = this.getAttribute('title-text-weight') || '600';
    const keyTextColor = this.getAttribute('key-text-color') || '#666666';
    const keyTextSize = this.getAttribute('key-text-size') || '14';
    const keyTextWeight = this.getAttribute('key-text-weight') || '400';
    const valueTextColor = this.getAttribute('value-text-color') || '#000000';
    const valueTextSize = this.getAttribute('value-text-size') || '14';
    const valueTextWeight = this.getAttribute('value-text-weight') || '500';
    const rowGap = this.getAttribute('row-gap') || '12';
    const rowBorderColor = this.getAttribute('row-border-color') || '#E0E0E0';
    const rowBorderWidth = this.getAttribute('row-border-width') || '1';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
        }

        .addon-plan-container {
          background-color: ${containerBgTransparent ? 'transparent' : containerBgColor};
          padding: ${containerPadding}px;
          border-radius: ${containerBorderRadius}px;
          display: flex;
          flex-direction: column;
          ${containerBorderWidth > 0 && containerBorderType === 'solid' ? `
            border: ${containerBorderWidth}px solid ${containerBorderColor};
          ` : ''}
          ${containerBorderWidth > 0 && containerBorderType === 'gradient' ? `
            border: ${containerBorderWidth}px solid transparent;
            background-image:
              linear-gradient(${containerBgTransparent ? 'transparent' : containerBgColor}, ${containerBgTransparent ? 'transparent' : containerBgColor}),
              linear-gradient(${containerBorderGradientAngle}deg, ${containerBorderGradientStart}, ${containerBorderGradientEnd});
            background-origin: padding-box, border-box;
            background-clip: padding-box, border-box;
          ` : ''}
        }

        .addon-plan-title {
          font-size: ${titleTextSize}px;
          font-weight: ${titleTextWeight};
          color: ${titleTextColor};
          margin: 0 0 ${rowGap}px 0;
        }

        .addon-plan-rows {
          display: flex;
          flex-direction: column;
        }

        .addon-plan-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          padding: ${rowGap}px 0;
          border-bottom: ${rowBorderWidth}px solid ${rowBorderColor};
        }

        .addon-plan-row:last-child {
          border-bottom: none;
        }

        .addon-plan-key {
          font-size: ${keyTextSize}px;
          font-weight: ${keyTextWeight};
          color: ${keyTextColor};
          text-align: left;
        }

        .addon-plan-value {
          font-size: ${valueTextSize}px;
          font-weight: ${valueTextWeight};
          color: ${valueTextColor};
          text-align: left;
        }
      </style>

      <div class="addon-plan-container">
        <h3 class="addon-plan-title">${planTitle}</h3>
        <div class="addon-plan-rows">
          <div class="addon-plan-row">
            <span class="addon-plan-key">${labelData}</span>
            <span class="addon-plan-value">${dataAmount}</span>
          </div>
          <div class="addon-plan-row">
            <span class="addon-plan-key">${labelDuration}</span>
            <span class="addon-plan-value">${duration}</span>
          </div>
          <div class="addon-plan-row">
            <span class="addon-plan-key">${labelExpires}</span>
            <span class="addon-plan-value">${expiresOn}</span>
          </div>
          <div class="addon-plan-row">
            <span class="addon-plan-key">${labelStatus}</span>
            <span class="addon-plan-value">${status}</span>
          </div>
        </div>
      </div>
    `;
  }
}

// Register the custom element
customElements.define('addon-plan-card', AddonPlanCard);
